/* eslint-disable */





import { normalize } from './normalizer';
import { tokenize } from './tokenizer';
import {
    extractPolicyId,
    extractPolicyTitle,
    extractSearchQuery,
    extractTemplateQuery,
    extractFrameworks,
    extractControlIds,
    extractTimeRange,
    extractKeywords,
} from './extractors';
import { scoreIntents } from './scorer';
import { resolveAmbiguity, buildActions } from './resolver';
import { buildHandoff } from './escalation';

/**
 * Parse a free-text policy module command from the command bar.
 *
 * Pipeline:
 *   normalize → tokenize → extract entities → score intents →
 *   resolve ambiguity → build actions → decide escalation → return ParseResult
 */
export function parsePolicyCommand(
    input,
    ctx,
) {
    const errors = [];

    // ── Step 1: Normalize ─────────────────────────────────────────────────────
    const normalized = normalize(input);
    const tokens = tokenize(normalized);

    // ── Step 3: Extract entities ──────────────────────────────────────────────
    const policy_id = extractPolicyId(normalized);

    // Framework extraction (requires token list + org-enabled frameworks)
    const { frameworks, ambiguous: frameworks_ambiguous } = extractFrameworks(
        normalized,
        tokens,
        ctx.org_enabled_frameworks,
    );

    // Control IDs depend on detected frameworks
    const control_ids = extractControlIds(normalized, frameworks);

    const policy_title = extractPolicyTitle(normalized, policy_id);
    const template_query = extractTemplateQuery(normalized);
    const time_range = extractTimeRange(normalized);

    // Search query extraction is intent-agnostic at this stage
    const search_query = extractSearchQuery(normalized);

    const keywords = extractKeywords(tokens, frameworks, control_ids);

    const entities = {
        policy_id,
        policy_title,
        template_query,
        search_query,
        frameworks,
        control_ids,
        time_range,
        keywords,
    };

    // ── Step 4: Score intents ─────────────────────────────────────────────────
    const candidates = scoreIntents(tokens, entities, ctx);

    // ── Step 5: Resolve ambiguity ─────────────────────────────────────────────
    const resolved = resolveAmbiguity(candidates);

    // ── Step 6: Build actions ─────────────────────────────────────────────────
    const baseActions = buildActions(resolved.intent, entities, ctx);

    // ── Step 7: Decide escalation ─────────────────────────────────────────────
    const { handoff, layer2_input, extraActions } = buildHandoff({
        intent: resolved.intent,
        confidence: resolved.confidence,
        normalized,
        tokens,
        entities,
        frameworks_ambiguous,
        ctx,
        actions: baseActions,
    });

    const actions = handoff.decision === 'ESCALATE_TO_LAYER2'
        ? [...baseActions, ...extraActions]
        : baseActions;

    // ── Step 8: Assemble ParseResult ──────────────────────────────────────────
    const result = {
        layer: 1,
        raw: input,
        normalized,

        intent: resolved.intent,
        confidence: resolved.confidence,
        reasons: resolved.reasons,

        policy_id,
        policy_title,
        template_query,
        search_query,
        frameworks,
        control_ids,
        time_range,
        keywords,

        actions,
        errors,

        handoff,
        ...(layer2_input ? { layer2_input } : {}),
    };

    return result;
}
